// src/hooks/useDeviceActions.js
import { useCallback } from "react";
import config from "../config";
import logger from "../utils/logger";

const COMMON_HEADERS = {
  "Content-Type": "application/json",
  "x-api-key": config.apiKey,
};

/**
 * Custom hook for device action execution
 *
 * Provides functions to:
 * - Execute single device actions (LOCK, UNLOCK, FREEZE, etc.)
 * - Execute bulk lockdown actions across multiple devices
 * - Update device display names
 *
 * @param {Function} authenticatedFetch - Authenticated fetch wrapper from AuthContext
 * @param {Function} invalidateDeviceStateCache - Cache invalidation function
 * @returns {Object} Device action functions
 */
export const useDeviceActions = (
  authenticatedFetch,
  invalidateDeviceStateCache
) => {
  // Execute action on a single device
  const executeDeviceAction = useCallback(
    async (deviceId, actionType) => {
      if (!deviceId) {
        throw new Error("No device ID provided");
      }

      logger.log(
        `[SINGLE DEVICE ACTION] 🎯 Executing action: ${actionType} for device ${deviceId}`
      );
      logger.log(
        `[SINGLE DEVICE ACTION] 📍 Endpoint: ${config.apiEndpoint}/device-actions`
      );
      logger.log(
        `[SINGLE DEVICE ACTION] 📦 Payload:`,
        JSON.stringify({ deviceId, actionType }, null, 2)
      );

      try {
        const response = await authenticatedFetch(
          `${config.apiEndpoint}/device-actions`,
          {
            method: "POST",
            headers: COMMON_HEADERS,
            body: JSON.stringify({ deviceId, actionType }),
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ message: `Action failed: ${response.status}` }));
          throw new Error(
            errorData.message || `Action failed: ${response.status}`
          );
        }

        const result = await response.json();
        logger.log(
          `[SINGLE DEVICE ACTION] ✅ Action successful for device ${deviceId}:`,
          result
        );

        // Invalidate cache after device action to prevent stale state
        if (invalidateDeviceStateCache) {
          invalidateDeviceStateCache(deviceId);
        }

        return result;
      } catch (error) {
        logger.error(
          `[SINGLE DEVICE ACTION] ❌ Failed to execute action for device ${deviceId}:`,
          error
        );
        throw error;
      }
    },
    [authenticatedFetch, invalidateDeviceStateCache]
  );

  // Execute bulk lockdown action (FREEZE/UNFREEZE) across multiple devices
  const executeBulkLockdownAction = useCallback(
    async (installationId, actionType, selectedDeviceIds = []) => {
      logger.log(
        `[BULK ACTION] 🚨 Executing BULK ${actionType} for installation ${installationId}...`
      );
      logger.log(
        `[BULK ACTION] 📍 Endpoint: ${config.apiEndpoint}/bulk-lockdown`
      );
      logger.log(
        `[BULK ACTION] 📝 Received ${selectedDeviceIds.length} selected device IDs:`,
        selectedDeviceIds
      );

      const payload = {
        installationId,
        actionType, // FREEZE or UNFREEZE
        deviceIds: selectedDeviceIds.length > 0 ? selectedDeviceIds : undefined,
      };

      logger.log(
        `[BULK ACTION] 📦 Sending payload:`,
        JSON.stringify(payload, null, 2)
      );

      try {
        const response = await authenticatedFetch(
          `${config.apiEndpoint}/bulk-lockdown`,
          {
            method: "POST",
            headers: COMMON_HEADERS,
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          logger.error(`[BULK ACTION] ❌ Response not OK:`, errorText);
          throw new Error(
            `Bulk lockdown failed: ${response.status} ${response.statusText}`
          );
        }

        const result = await response.json();
        logger.log(`[BULK ACTION] ✅ Bulk action response:`, result);

        return result;
      } catch (error) {
        logger.error(`[BULK ACTION] ❌ Failed to execute bulk action:`, error);
        throw error;
      }
    },
    [authenticatedFetch]
  );

  // Update device display name
  const updateDeviceDisplayName = useCallback(
    async (deviceId, newName) => {
      if (!deviceId) {
        throw new Error("No device ID provided");
      }

      logger.log(`Updating display name for device ${deviceId} to: ${newName}`);

      try {
        const response = await authenticatedFetch(
          `${config.apiEndpoint}/device-display-name`,
          {
            method: "POST",
            headers: COMMON_HEADERS,
            body: JSON.stringify({ deviceId, newDisplayName: newName }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update display name: ${response.status}`);
        }

        const result = await response.json();
        logger.log(`Successfully updated display name:`, result);

        // Invalidate cache after name update
        if (invalidateDeviceStateCache) {
          invalidateDeviceStateCache(deviceId);
        }

        return result;
      } catch (error) {
        logger.error("Failed to update device display name:", error);
        throw error;
      }
    },
    [authenticatedFetch, invalidateDeviceStateCache]
  );

  // Update firmware on a single device
  const updateFirmware = useCallback(
    async (deviceId) => {
      if (!deviceId) {
        throw new Error("No device ID provided");
      }

      logger.log(
        `[FIRMWARE UPDATE] 🔄 Initiating firmware update for device ${deviceId}`
      );
      logger.log(
        `[FIRMWARE UPDATE] 📍 Endpoint: ${config.servicesApiEndpoint}/firmware-update`
      );

      try {
        const response = await authenticatedFetch(
          `${config.servicesApiEndpoint}/firmware-update`,
          {
            method: "POST",
            headers: COMMON_HEADERS,
            body: JSON.stringify({ deviceId }),
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ message: `Firmware update failed: ${response.status}` }));
          throw new Error(
            errorData.message || `Firmware update failed: ${response.status}`
          );
        }

        const result = await response.json();
        logger.log(
          `[FIRMWARE UPDATE] ✅ Firmware update initiated for device ${deviceId}:`,
          result
        );

        return result;
      } catch (error) {
        logger.error(
          `[FIRMWARE UPDATE] ❌ Failed to update firmware for device ${deviceId}:`,
          error
        );
        throw error;
      }
    },
    [authenticatedFetch]
  );

  // Update a single credential reader setting (NFC/Mifare toggle)
  const updateCredentialsSetting = useCallback(
    async ({ deviceId, setting, value }) => {
      const response = await authenticatedFetch(
        `${config.apiEndpoint}/device-credentials-settings`,
        {
          method: "POST",
          headers: COMMON_HEADERS,
          body: JSON.stringify({ deviceId, setting, value }),
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    },
    [authenticatedFetch]
  );

  return {
    executeDeviceAction,
    executeBulkLockdownAction,
    updateDeviceDisplayName,
    updateFirmware,
    updateCredentialsSetting,
  };
};
